/**
 * Tenant Storage — Namespaces localStorage by tenant code in Electron mode.
 *
 * Monkey-patches localStorage.getItem/setItem/removeItem to prefix keys with
 * `tenant_{code}_`. Global keys (exempted from namespacing) are listed below.
 */

// Keys that should NOT be namespaced (device-level, not tenant-level)
const GLOBAL_KEYS = new Set([
  "userPreferences",
  "cw-splash-shown",
  "cw-active-tenant",
]);

let _initialized = false;
let _tenantPrefix = null;
let _originalGetItem = null;
let _originalSetItem = null;
let _originalRemoveItem = null;

/**
 * Initialize tenant-scoped localStorage.
 * Only effective in Electron mode. Safe to call multiple times.
 */
export function initTenantStorage(tenantCode) {
  if (!tenantCode) return;

  const prefix = `tenant_${tenantCode}_`;

  // If already initialized with same prefix, skip
  if (_initialized && _tenantPrefix === prefix) return;

  // If switching tenant prefix, restore originals first
  if (_initialized) {
    restoreTenantStorage();
  }

  _tenantPrefix = prefix;
  _originalGetItem = localStorage.getItem.bind(localStorage);
  _originalSetItem = localStorage.setItem.bind(localStorage);
  _originalRemoveItem = localStorage.removeItem.bind(localStorage);

  localStorage.getItem = function (key) {
    if (GLOBAL_KEYS.has(key)) return _originalGetItem(key);
    return _originalGetItem(prefix + key);
  };

  localStorage.setItem = function (key, value) {
    if (GLOBAL_KEYS.has(key)) return _originalSetItem(key, value);
    return _originalSetItem(prefix + key, value);
  };

  localStorage.removeItem = function (key) {
    if (GLOBAL_KEYS.has(key)) return _originalRemoveItem(key);
    return _originalRemoveItem(prefix + key);
  };

  _initialized = true;
  console.debug(`[TenantStorage] Namespaced localStorage with prefix: ${prefix}`);
}

/**
 * Restore original localStorage methods (used when switching tenants).
 */
export function restoreTenantStorage() {
  if (!_initialized) return;
  if (_originalGetItem) localStorage.getItem = _originalGetItem;
  if (_originalSetItem) localStorage.setItem = _originalSetItem;
  if (_originalRemoveItem) localStorage.removeItem = _originalRemoveItem;
  _initialized = false;
  _tenantPrefix = null;
}
